<template>
  <div>
    <h5 class="title is-5">{{ $t("projects.list") }}</h5>
    <div>
      <AlertMessage
        color="is-info"
        :message="$t('projects.listMessage')"
      ></AlertMessage>
      <vue-element-loading :active="isLoading" spinner="spinner" />
      <div class="columns">
        <div class="column is-one-third">
          <div class="control has-icons-left">
            <input
              class="input is-normal"
              type="text"
              placeholder="Search"
              v-model="searchQuery"
              @input="currentPage = 1"
            />
            <span class="icon is-small is-left">
              <i class="fas fa-search"></i>
            </span>
          </div>
        </div>
      </div>
      <table class="table is-fullwidth is-striped">
        <thead>
          <tr>
            <th width="50%">{{ $t("projects.projectName") }}</th>
            <th>{{ $t("projects.projectId") }}</th>
            <th>{{ $t("projects.userRole") }}</th>
            <th v-if="isViewSecondaryUse">{{ $t("projects.secondaryUse") }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, projectId) in filteredLists" :key="projectId">
            <td>
              <a @click="selectProject(item)">
                {{ item.projectName }}
              </a>
            </td>
            <td>{{ item.projectId }}</td>
            <td>{{ item.userRole }}</td>
            <td v-if="isViewSecondaryUse">
              <span v-if="item.secondaryUse == 'true'">{{
                $t("projects.secondaryUseEnabled")
              }}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <nav
      v-if="isPager == true"
      class="pagination is-centered is-small"
      role="navigation"
      aria-label="pagination"
    >
      <paginate
        v-model="currentPage"
        :page-count="getPageCount"
        :click-handler="clickCallback"
        :page-range="3"
        :margin-pages="2"
        :prev-text="'<'"
        :next-text="'>'"
        :page-class="'page-item'"
        :container-class="'pagination-list'"
        :page-link-class="'pagination-link'"
      >
      </paginate>
    </nav>
  </div>
</template>
<script>
import AlertMessage from "@/components/AlertMessage.vue";
import imsr from "@/imsr-api";
import { computed, ref } from "vue";
import VueElementLoading from "vue-element-loading";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import Paginate from "vuejs-paginate-next";

export default {
  components: {
    AlertMessage,
    VueElementLoading,
    Paginate,
  },
  setup() {
    const store = useStore();
    const router = useRouter();
    const projectList = ref([]);
    const tenantId = ref(computed(() => store.getters.tenantId));
    const projectId = ref(computed(() => store.getters.projectId));
    const userId = ref(computed(() => store.getters.userId));
    const userRole = ref(computed(() => store.getters.userRole));
    const isLoading = ref(false);
    const searchQuery = ref("");
    const currentPage = ref(1); // 現在のページ
    const perPage = ref(50); // 1ページあたりのアイテム数
    const isViewSecondaryUse = ref(false); // 二次利用を表示するかどうか

    const decideViewSecondaryUse = async () => {
      try {
        isLoading.value = true;
        const tenant = await imsr.imsrGetApi("/tenants", {
          tenantId: store.getters.tenantId,
        });
        if (
          userRole.value == "admin" && // スペース管理者権限
          tenant.secondaryUse === true && // 二次利用が有効
          tenant.plan === "standard" // スタンダードプラン
        ) {
          isViewSecondaryUse.value = true;
        } else {
          isViewSecondaryUse.value = false;
        }
        isLoading.value = false;
      } catch (e) {
        router.push({ name: "InternalServerError" });
      }
    };
    decideViewSecondaryUse();

    const getList = async () => {
      try {
        isLoading.value = true;
        projectList.value = await imsr.imsrGetApi("/projects/list", {
          tenantId: tenantId.value,
        });
        isLoading.value = false;
      } catch (e) {
        router.push({ name: "InternalServerError" });
      }
    };
    getList();

    const selectProject = async (item) => {
      try {
        const { projectId, projectName, userRole, timezone, zoneinfo } = item;
        store.commit("setProjectId", projectId);
        store.commit("setProjectName", projectName);
        store.commit("setProjectRole", userRole);
        store.commit("setTimezone", timezone);
        store.commit("setZoneinfo", zoneinfo);
        const params = {
          tenantId: tenantId.value,
          projectId: projectId,
        };

        await imsr.imsrPutApi("/users/lastProject", params);
        router.push({ name: "dashboard", params: { projectId: projectId } });
      } catch (e) {
        router.push({ name: "InternalServerError" });
      }
    };

    const filteredProjects = computed(() => {
      return projectList.value.filter(
        (project) =>
          project.projectId
            .toLowerCase()
            .includes(searchQuery.value.toLowerCase()) ||
          project.projectName
            .toLowerCase()
            .includes(searchQuery.value.toLowerCase())
      );
    });

    // ページネーションに基づいてリストを取得
    const filteredLists = computed(() => {
      let end = currentPage.value * perPage.value;
      let start = end - perPage.value;
      return filteredProjects.value.slice(start, end);
    });

    const getPageCount = computed(() => {
      return Math.ceil(filteredProjects.value.length / perPage.value);
    });

    const isPager = computed(() => {
      return filteredProjects.value.length > perPage.value;
    });

    const clickCallback = (pageNum) => {
      currentPage.value = Number(pageNum); //ページネーションで選択したページ数
    };

    return {
      projectList,
      selectProject,
      tenantId,
      projectId,
      userId,
      isLoading,
      searchQuery,
      isPager,
      filteredLists,
      getPageCount,
      clickCallback,
      currentPage,
      isViewSecondaryUse,
    };
  },
};
</script>
